import { PopoverContent } from '@radix-ui/react-popover';
import { directionalProperty, rem } from 'polished';
import styled, { css } from 'styled-components';

import { DownArrowSmallIcon } from '../../icons';
import { zIndexes } from '../../theme';
import Checkbox from '../Checkbox/Checkbox';
import { CheckboxSpacingKey } from '../Checkbox/constants';
import { SearchInput } from '../Input';
import ListItem, { ListItemVariableNames } from '../ListItem';
import { ListItemProps } from '../ListItem/ListItem';

export const DropdownRoot = styled.div`
  position: relative;
  width: 100%;
  height: max-content;
  border-radius: ${({ theme }) => theme.core.radius['3x']};
  user-select: none;
`;

export const DropdownPopoverContent = styled(PopoverContent)(({
  theme,
  'aria-expanded': isExpanded,
}) => {
  const variant = theme.components.Dropdown.List;

  return css`
    z-index: ${zIndexes.modal};
    box-sizing: border-box;
    max-height: ${isExpanded ? rem('220px') : '0'};
    overflow-y: auto;
    border-radius: ${variant.radius};

    /*
     note: for some reason there is 2px height still visible when popover is collapsed,
     so we set border and bg color to transparent as workaround so this 2px is not visible
    */
    border: 1px solid ${isExpanded ? variant.border : 'transparent'};
    background-color: ${isExpanded ? variant.bg : 'transparent'};
    color: ${variant.fg};
    transition-timing-function: ease-out;
    transition-duration: 0.1s;
    transition-property: max-height, border, background-color;
    overscroll-behavior-y: contain;
  `;
});

export type DropdownListItemProps = ListItemProps & {
  $isMultiselect?: boolean;
};

const dropdownListItemVariableStyles = css<DropdownListItemProps>(
  ({ theme, $isMultiselect, hasTransparentBackground }) => {
    const variant = theme.components.Dropdown.ListItem;

    return {
      [ListItemVariableNames.BackgroundColor]: hasTransparentBackground
        ? 'transparent'
        : variant['bg-default'],
      [ListItemVariableNames.HoverBackgroundColor]: variant['bg-hover'],
      [ListItemVariableNames.FocusBorderColor]: variant.shadow,
      [ListItemVariableNames.Color]: variant['fg-default'],
      [ListItemVariableNames.HorizontalPadding]: $isMultiselect
        ? `calc(${variant['horizontal-padding']} + ${theme.core.spacing[CheckboxSpacingKey]})`
        : variant['horizontal-padding'],
      [ListItemVariableNames.Gap]: variant.inside,
      [ListItemVariableNames.Height]: variant.height,
      [ListItemVariableNames.FontSize]: variant['font-size'],
    };
  },
);

export const DropdownListItem = styled(ListItem)<DropdownListItemProps>`
  ${dropdownListItemVariableStyles}
`;

export const DropdownListItemCheckbox = styled(Checkbox)`
  width: 100%;
  height: 100%;
  padding: 0;
  gap: ${({ theme }) =>
    `calc(var(${ListItemVariableNames.Gap}) + ${theme.core.spacing[CheckboxSpacingKey]})`};
`;

export const IconContainer = styled.span.attrs<{ $src?: string }>(props =>
  props.$src
    ? {
        style: {
          // note: for performance reasons it's important that `backgroundImage` is inline style,
          // otherwise we could have over 200 classes generated for dropdowns with lot of elements
          backgroundImage: `url("${props.$src}")`,
        },
      }
    : {},
)`
  ${({ $src }) =>
    $src &&
    css`
      padding-left: calc(
        var(${ListItemVariableNames.IconSize}) +
          var(${ListItemVariableNames.Gap})
      );
      background-repeat: no-repeat;
      background-position-y: center;
      background-size: var(${ListItemVariableNames.IconSize});
    `}

  display: flex;
  gap: var(${ListItemVariableNames.Gap});
  align-items: center;
`;

export const NoResults = styled.span`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  height: ${({ theme }) => theme.components.Dropdown.ListItem.height};
  padding: 0
    ${({ theme }) => theme.components.Dropdown.ListItem['horizontal-padding']};
  font-weight: ${({ theme }) => theme.core.typography.fontWeight.regular};
  font-size: ${({ theme }) => theme.components.Dropdown.ListItem['font-size']};
  line-height: ${rem(18)};
`;

export const ArrowIcon = styled(DownArrowSmallIcon)<{ $isOpen?: boolean }>`
  transform: ${({ $isOpen }) => `rotate(${$isOpen ? -180 : 0}deg)`};
  transition: transform 0.3s ease-in-out;
  pointer-events: none;
`;

export const DropdownSearchInput = styled(SearchInput)`
  box-sizing: border-box;
  ${({ theme }) =>
    directionalProperty(
      'padding',
      theme.components.Dropdown.ListItem['horizontal-padding'],
      theme.components.Dropdown.ListItem['horizontal-padding'],
      theme.core.spacing['4x'],
    )}
`;

export const SpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  height: ${({ theme }) => theme.components.Dropdown.ListItem.height};
  padding-left: ${({ theme }) =>
    theme.components.Dropdown.ListItem['horizontal-padding']};
`;
